import { WorkSurfaceSize } from "../../../game/bench-work/workpiece";
import { Direction } from "../../../game/Vectors";
import { PIXELS_PER_INCH } from "../../../views/shop-scale";

/**
 * The bench stage: the measured canvas the workpiece is fitted into,
 * zoomed well past the shop view's 4 px/inch — leaning over the bench.
 * The canvas takes whatever size the sheet gives it (measured by the
 * wrapper, rendered at device resolution — no CSS upscale, no blur), so
 * every fit is computed from real pixels. All pointer work happens in
 * workpiece inches; these helpers own the mapping so every surface
 * component shares one idea of where the wood is.
 */

/** The rectangle of the canvas a workpiece may occupy, in stage px. */
export interface StageRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Padding kept around the frame so its edges stay strokable. Slim,
 * because the caller has already reserved the bands its chrome floats
 * in and the frame carries its own margin in inches — this is the last
 * few pixels of grab room, not the breathing space.
 */
const STAGE_MARGIN = 16;

/** Leaning in only goes so far — cap the zoom for tiny pieces. */
/** Exported because it is also the ceiling on useful texture detail:
 * nothing in the game ever magnifies a material past this, so
 * `npm run process:textures` ships face art sized against it rather than
 * at whatever resolution the photograph happened to be cropped at. */
export const MAX_PX_PER_IN = 40;

export interface StageFit {
  /** Pixels per workpiece inch at this zoom. */
  readonly pxPerIn: number;
  /** Scale factor for material sprites (drawn at PIXELS_PER_INCH). */
  readonly spriteScale: number;
  /** The workpiece's top-left corner on the stage, in pixels. */
  readonly originX: number;
  readonly originY: number;
  readonly widthIn: number;
  readonly heightIn: number;
}

/** Fit a workpiece (inches) into a stage rectangle, centered. */
export function fitToStage(size: WorkSurfaceSize, rect: StageRect): StageFit {
  const pxPerIn = Math.min(
    (rect.width - STAGE_MARGIN * 2) / size.widthIn,
    (rect.height - STAGE_MARGIN * 2) / size.heightIn,
    MAX_PX_PER_IN,
  );
  return {
    pxPerIn,
    spriteScale: pxPerIn / PIXELS_PER_INCH,
    originX: rect.x + (rect.width - size.widthIn * pxPerIn) / 2,
    originY: rect.y + (rect.height - size.heightIn * pxPerIn) / 2,
    widthIn: size.widthIn,
    heightIn: size.heightIn,
  };
}

/** Where the run stands on the shop floor, as the camera draws it. */
export interface FloorSeat {
  /** The run's middle, in screen px. */
  readonly x: number;
  readonly y: number;
  /** How big an inch of bench is out on the floor, as a fraction of
   * the same inch on the stage. */
  readonly scale: number;
  /** Which way the run faces on the floor (`BenchGroup.alignment`). */
  readonly alignment: Direction;
}

/** The transform the stage's frame wears partway through the dive. */
export interface LeanIn {
  readonly scale: number;
  /** PIXI degrees, clockwise on screen. */
  readonly angleDeg: number;
  /** The frame's middle in stage px: the point it turns and grows about. */
  readonly pivotX: number;
  readonly pivotY: number;
  /** Where that middle sits on screen. */
  readonly x: number;
  readonly y: number;
}

/**
 * The angle a bench facing `alignment` is drawn at on the shop floor,
 * the same quarter turns `MachineView` gives its art. Three turns one
 * way is one turn the other, so the dive unwinds the short way round.
 */
export function floorAngleDeg(alignment: Direction): number {
  const turns = alignment === 3 ? -1 : alignment;
  return turns * -90;
}

/**
 * The lean-in, `eased` of the way through: at 0 the frame is mapped
 * onto the bench's footprint where it stands on the floor — as small,
 * as far off-center, and as turned as the shop draws it — and at 1 it
 * is the stage itself, upright and full size. The frame turns as it
 * grows, so a bench standing sideways on the floor swings square to
 * the screen as the player leans over it, and swings back on the way
 * out. Everything is about the frame's middle, so the picture stays
 * put on the bench while it turns.
 */
export function leanInTransform(
  eased: number,
  floor: FloorSeat,
  fit: StageFit,
): LeanIn {
  const pivotX = fit.originX + (fit.widthIn / 2) * fit.pxPerIn;
  const pivotY = fit.originY + (fit.heightIn / 2) * fit.pxPerIn;
  return {
    scale: floor.scale + (1 - floor.scale) * eased,
    angleDeg: floorAngleDeg(floor.alignment) * (1 - eased),
    pivotX,
    pivotY,
    x: floor.x + (pivotX - floor.x) * eased,
    y: floor.y + (pivotY - floor.y) * eased,
  };
}

/**
 * A DOM pointer event's position in workpiece inches. The canvas renders
 * at its CSS size (autoDensity), so client px map 1:1 onto stage px.
 */
export function pointerToInches(
  fit: StageFit,
  rect: DOMRect,
  clientX: number,
  clientY: number,
): { xIn: number; yIn: number } {
  return {
    xIn: (clientX - rect.left - fit.originX) / fit.pxPerIn,
    yIn: (clientY - rect.top - fit.originY) / fit.pxPerIn,
  };
}

/**
 * Per-cell accumulation for one stroke event: the tool's coverage rate
 * enforced against how far the pointer actually moved. A slow, deliberate
 * stroke saturates as it goes (capped at 1); a fast scrub spreads the
 * same work thin and needs more passes — which is exactly the feel the
 * tool tiers buy (see OperationInteraction.coveragePerSecond).
 */
export function strokeGain(
  coveragePerSecond: number,
  radiusIn: number,
  distanceIn: number,
  dtMs: number,
): number {
  const sweptArea = Math.max(distanceIn, 0.01) * radiusIn * 2;
  const workBudget = (coveragePerSecond * Math.min(dtMs, 100)) / 1000;
  // Stamps along the segment overlap ~4× at half-radius spacing
  return Math.min(1, workBudget / sweptArea) / 4;
}

/**
 * Per-cell accumulation for one frame of a powered tool resting in
 * place: the same coverage rate spent over the pad's own footprint
 * instead of a swept band — the orbit sander cutting the spot it sits
 * on. Halved because a zero-length stampStroke lands the point twice.
 */
export function dwellGain(
  coveragePerSecond: number,
  radiusIn: number,
  dtMs: number,
): number {
  const padArea = Math.PI * radiusIn * radiusIn;
  const workBudget = (coveragePerSecond * Math.min(dtMs, 100)) / 1000;
  return Math.min(1, workBudget / padArea) / 2;
}

/**
 * The tween the pieces turn with: the spring the old scene ran (tension
 * 300, friction 26) — a hand turning the piece, no bounce. Steps one
 * value toward its target and snaps when it's close enough to be done.
 * The step is capped at 50ms: integrated forward-Euler, this spring
 * diverges past a tenth of a second, so a long frame (tab switch,
 * breakpoint, a starved renderer) plays less motion instead of
 * exploding.
 */
export function stepTurnSpring(
  value: number,
  velocity: number,
  target: number,
  dt: number,
): [number, number] {
  const step = Math.min(dt, 0.05);
  const nextVelocity =
    velocity + (300 * (target - value) - 26 * velocity) * step;
  const next = value + nextVelocity * step;
  return Math.abs(target - next) < 0.01 && Math.abs(nextVelocity) < 0.05
    ? [target, 0]
    : [next, nextVelocity];
}

/**
 * A pried nail on its way out of the wood, in pixels along the claw's
 * lever from the hole: the stretch of shank showing runs from `from`
 * (at the hole until the point clears it) to `to` (the head). The pull
 * eases out — fast as the head lifts, slowing as the point comes free —
 * and ends with the whole nail `clearancePx` clear of the hole, where
 * the flight to the tally picks it up.
 */
export function nailPullFrame(
  progress: number,
  nailLengthPx: number,
  clearancePx: number,
): { from: number; to: number } {
  const t = Math.min(1, Math.max(0, progress));
  const eased = 1 - (1 - t) * (1 - t);
  const to = eased * (nailLengthPx + clearancePx);
  return { from: Math.max(0, to - nailLengthPx), to };
}
